/**
 * @file Error / non-success terminal screen. Renders a generalized message
 * per `failure_reason` category (no internal details — see
 * `feedback_no_internal_errors_in_ui.md`).
 */

import type { ExportFailureCategory, ExportPhase } from "../types";

const HEADLINES: Record<ExportPhase, string> = {
  loading: "読み込み中…",
  awaiting_approval: "承認をお待ちしています",
  approved: "承認されました",
  consuming: "セッションを開始しています…",
  exporting: "エクスポート中…",
  completed: "エクスポートが完了しました",
  failed: "エクスポートに失敗しました",
  rejected: "モバイル側で接続が拒否されました",
  expired: "セッションの有効期限が切れました",
};

const FAILURE_BLURBS: Record<ExportFailureCategory, string> = {
  network_error: "ネットワーク接続を確認してから、もう一度お試しください。",
  firestore_permission_error:
    "セッションの読み書き権限がありませんでした。モバイルアプリで再発行してください。",
  storage_permission_error:
    "メディアの読み取り権限がありませんでした。モバイルアプリで再発行してください。",
  decryption_error: "セッションの鍵情報が一致しませんでした。モバイル QR を読み直してください。",
  disk_write_error: "ファイルの書き出しに失敗しました。空き容量を確認してください。",
  user_cancelled: "保存がキャンセルされました。再度お試しください。",
  browser_unsupported:
    "このブラウザでは保存ダイアログを表示できません。Chrome / Edge をご利用ください。",
  session_invalid: "セッションが見つかりませんでした。モバイルでもう一度発行してください。",
};

export function ExportError({
  phase,
  category,
}: {
  phase: ExportPhase;
  category: ExportFailureCategory | null;
}) {
  const headline = HEADLINES[phase];
  const blurb = category ? FAILURE_BLURBS[category] : undefined;
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <h1 style={{ marginTop: 0 }}>{headline}</h1>
      {blurb && <p style={{ color: "var(--color-text-sub)", lineHeight: 1.7 }}>{blurb}</p>}
    </section>
  );
}
